import React, { useEffect, useId, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { alpha, keyframes } from "@mui/material/styles";
import { JustInfo, StyledIconButton } from "../../components/utils/Icon";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(96, 63, 151, 0.35); }
  70% { box-shadow: 0 0 0 8px rgba(96, 63, 151, 0); }
  100% { box-shadow: 0 0 0 0 rgba(96, 63, 151, 0); }
`;

const DEFAULT_DIALOG_HEIGHT = 620;

/**
 * Reusable multi-step process guide dialog.
 *
 * steps: [{
 *   label: string,
 *   icon: MuiSvgIconComponent,
 *   title: string,
 *   guidelines: [{
 *     label?: string,
 *     text?: string,
 *     details?: [{ label?: string, text: string }],
 *   }],
 *   reminder?: string,
 * }]
 */
const ProcessGuideDialog = ({
  open,
  onClose,
  title,
  titleIcon: TitleIcon = AutoAwesomeIcon,
  steps = [],
  initialFlowIndex = 0,
  confirmLabel = "Got it",
  closeTooltip = "Close guide",
  tabsAriaLabel = "Guide stages",
  dialogHeight = DEFAULT_DIALOG_HEIGHT,
}) => {
  const guideId = useId();
  const useSideNavigation = useMediaQuery("(min-width:600px)");
  const stepCount = steps.length;
  const clampedIndex = Math.min(
    Math.max(stepCount - 1, 0),
    Math.max(0, Number(initialFlowIndex) || 0),
  );
  const [selected, setSelected] = useState(clampedIndex);
  const step = steps[selected] || steps[0];
  const Icon = step?.icon;

  useEffect(() => {
    if (open) setSelected(clampedIndex);
  }, [open, clampedIndex]);

  if (!stepCount || !step) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      scroll='paper'
      aria-labelledby={`${guideId}-title`}
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
          maxWidth: 840,
          height: dialogHeight,
          maxHeight: { xs: "calc(100dvh - 16px)", sm: "calc(100dvh - 32px)" },
          overflow: "hidden",
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          alignItems: "flex-start",
          display: "flex",
          gap: 1.5,
          px: { xs: 2.5, sm: 3 },
          py: 2.5,
          flexShrink: 0,
        }}
      >
        <Box
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: 2,
            color: "primary.main",
            display: "grid",
            flexShrink: 0,
            height: 40,
            placeItems: "center",
            width: 40,
          })}
        >
          <TitleIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography id={`${guideId}-title`} variant='h6' fontWeight={700}>
            {title}
          </Typography>
        </Box>
        <StyledIconButton
          tooltip={closeTooltip}
          icon={CloseIcon}
          onClick={onClose}
          iconColor='text.secondary'
          sx={{ height: 32, width: 32, flexShrink: 0 }}
        />
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={selected}
          onChange={(_event, nextValue) => setSelected(nextValue)}
          variant='scrollable'
          orientation={useSideNavigation ? "vertical" : "horizontal"}
          allowScrollButtonsMobile
          scrollButtons='auto'
          aria-label={tabsAriaLabel}
          sx={{
            flexShrink: 0,
            width: { xs: "100%", sm: 184 },
            bgcolor: "background.default",
            borderRight: { sm: "1px solid" },
            borderBottom: { xs: "1px solid", sm: 0 },
            borderColor: "divider",
            py: { xs: 0.5, sm: 2 },
            px: { xs: 0.5, sm: 1 },
            "& .MuiTabs-flexContainer": { gap: 0.75 },
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              fontWeight: 500,
              minHeight: 56,
              borderRadius: 1,
              minWidth: 0,
              px: 1.5,
              textTransform: "none",
              alignItems: "flex-start",
              "&.Mui-selected": {
                fontWeight: 700,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            },
          }}
        >
          {steps.map((item, index) => (
            <Tab
              key={item.label || index}
              value={index}
              label={
                <Box
                  component='span'
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    textAlign: "left",
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: 24,
                      height: 24,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor:
                        selected === index ? "primary.main" : "action.hover",
                      color:
                        selected === index
                          ? "primary.contrastText"
                          : "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  {item.label}
                </Box>
              }
              id={`${guideId}-tab-${index}`}
              aria-controls={`${guideId}-panel-${index}`}
            />
          ))}
        </Tabs>

        <Box
          key={selected}
          role='tabpanel'
          id={`${guideId}-panel-${selected}`}
          aria-labelledby={`${guideId}-tab-${selected}`}
          tabIndex={0}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            scrollbarGutter: "stable",
            overflowWrap: "anywhere",
            px: { xs: 2.5, sm: 4 },
            py: 3,
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: "block", mb: 0.5 }}
          >
            Step {selected + 1} of {stepCount}
          </Typography>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 3 }}>
            {Icon ? (
              <Icon sx={{ fontSize: 22, color: "primary.main", flexShrink: 0 }} />
            ) : null}
            <Typography
              variant='h6'
              component='h3'
              fontWeight={700}
              sx={{ lineHeight: 1.35 }}
            >
              {step.title}
            </Typography>
          </Stack>

          <Box component='dl' sx={{ m: 0 }}>
            {(step.guidelines || []).map((item, index) => (
              <Box
                key={item.label || index}
                sx={{
                  mt: index === 0 ? 0 : 3,
                }}
              >
                {item.label ? (
                  <Typography component='dt' variant='body2' fontWeight={700}>
                    {item.label}
                  </Typography>
                ) : null}
                <Box component='dd' sx={{ m: 0, mt: item.label ? 0.5 : 0 }}>
                  {item.text && (
                    <Typography variant='body2' sx={{ lineHeight: 1.5 }}>
                      {item.text}
                    </Typography>
                  )}
                  {item.details && (
                    <Box
                      sx={{
                        mt: item.text ? 1 : 0,
                        pl: 1.25,
                        borderLeft: "2px solid",
                        borderColor: "divider",
                      }}
                    >
                      {item.details.map((detail, detailIndex) => (
                        <Typography
                          key={detail.label || detail.text || detailIndex}
                          variant='body2'
                          color='text.secondary'
                          sx={{
                            lineHeight: 1.5,
                            mt: detailIndex === 0 ? 0 : 0.75,
                          }}
                        >
                          {detail.label && (
                            <Box component='span' sx={{ fontWeight: 600 }}>
                              {detail.label}:{" "}
                            </Box>
                          )}
                          {detail.text}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {step.reminder ? (
            <Box
              role='note'
              sx={{
                mt: 3,
                p: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
                borderLeft: "3px solid",
                borderLeftColor: "info.main",
                borderRadius: 1,
              }}
            >
              <Box sx={{ flexShrink: 0, lineHeight: 1, pt: 0.125 }}>
                <JustInfo title='Reminder' ml={0} mr={0} />
              </Box>
              <Typography
                variant='body2'
                sx={{ lineHeight: 1.5, fontWeight: 500, minWidth: 0 }}
              >
                {step.reminder}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant='contained'
          onClick={onClose}
          sx={{
            textTransform: "none",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProcessGuideTrigger = ({
  highlighted = false,
  compact = false,
  initialFlowIndex = 0,
  tooltip = "Open guide",
  ...dialogProps
}) => {
  const [open, setOpen] = useState(false);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  return (
    <>
      <StyledIconButton
        onClick={() => setOpen(true)}
        tooltip={tooltip}
        icon={HelpOutlineIcon}
        iconSize={compact ? 19 : 21}
        iconColor='primary.main'
        sx={{
          animation:
            highlighted && !reduceMotion
              ? `${pulse} 2.2s ease-out infinite`
              : "none",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          border: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
          height: compact ? 32 : 36,
          p: 0,
          transition: "background-color 0.2s ease, border-color 0.2s ease",
          width: compact ? 32 : 36,
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            borderColor: "primary.main",
          },
        }}
      />
      <ProcessGuideDialog
        open={open}
        onClose={() => setOpen(false)}
        initialFlowIndex={initialFlowIndex}
        {...dialogProps}
      />
    </>
  );
};

const ProcessGuideBanner = ({
  title,
  description,
  infoTooltip,
  ctaLabel = "View guide",
  initialFlowIndex = 0,
  ...dialogProps
}) => {
  const [open, setOpen] = useState(false);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  return (
    <>
      <Paper
        variant='outlined'
        component='button'
        type='button'
        onClick={() => setOpen(true)}
        sx={{
          alignItems: "center",
          animation: reduceMotion ? "none" : `${pulse} 2.2s ease-out 1`,
          appearance: "none",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
          color: "inherit",
          cursor: "pointer",
          display: "flex",
          fontFamily: "inherit",
          gap: 1,
          mb: 2.5,
          p: { xs: 1.25, sm: 1.5 },
          textAlign: "left",
          width: "100%",
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            borderColor: "primary.main",
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
            borderRadius: 1.5,
            display: "flex",
            flexShrink: 0,
            height: 34,
            justifyContent: "center",
            width: 34,
          }}
        >
          <JustInfo title={infoTooltip || description || title} ml={0} mr={0} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='subtitle2' fontWeight={800}>
            {title}
          </Typography>
          {description ? (
            <Typography variant='body2' color='text.secondary'>
              {description}
            </Typography>
          ) : null}
        </Box>
        <Typography
          variant='caption'
          color='primary.main'
          fontWeight={800}
          sx={{ display: { xs: "none", sm: "block" }, flexShrink: 0 }}
        >
          {ctaLabel}
        </Typography>
      </Paper>
      <ProcessGuideDialog
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        initialFlowIndex={initialFlowIndex}
        {...dialogProps}
      />
    </>
  );
};

export {
  ProcessGuideBanner,
  ProcessGuideDialog,
  ProcessGuideTrigger,
};
export default ProcessGuideDialog;
